'use client';

/**
 * ThinkingBlock — a turn's intermediate reasoning as an in-chat, borderless
 * "Thinking" timeline (no white bubble). It is fed BOTH the turn's commentary
 * lines AND the turn's RAG references, from which it builds semantic steps:
 * a globe "consulted sources" step, a media step (video/article chip), and a
 * sparkle step per commentary line.
 *
 * Pure presentational. The parent (page.tsx) decides which commentary + refs
 * belong to a turn (via groupMessagesForRender) and whether the block is live
 * (inProgress) for the currently-responding turn.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { RAGReference } from '@/types/rag';
import { cn } from '@/lib/utils';
import { FadeSlideIn } from './fade-slide-in';
import { buildThinkingModel } from './thinking/thinking-steps';
import {
  ThinkingCollapsed,
  ThinkingTimeline,
} from './thinking/thinking-timeline';

interface ThinkingBlockProps {
  inProgress: boolean;
  /** True once the turn's final answer has begun — collapses the block. */
  answerStarted: boolean;
  commentary: string[];
  refs: RAGReference[];
  /** Worked span for a RESTORED turn, derived from persisted row timestamps
   *  (thinking-groups). Live turns pass null and are measured here instead. */
  workedSeconds?: number | null;
  /** Epoch-ms the turn's user message was sent (thinking-groups). Anchors the
   *  live clock at send time — this block only mounts once the first
   *  commentary arrives, which would undercount the span the user actually
   *  waited. */
  turnStartedAt?: number | null;
  /** [Turn 18] The running tool's name while this turn is live
   *  (turn_state.tool) — keys the header's fixed-vocabulary status label
   *  (thinkingStatusLabel). Null/absent → plain "Thinking". */
  liveTool?: string | null;
}

export default function ThinkingBlock({
  inProgress,
  answerStarted,
  commentary,
  refs,
  workedSeconds = null,
  turnStartedAt = null,
  liveTool = null,
}: ThinkingBlockProps) {
  // Open while the commentary streams (before the answer); collapse the moment
  // the answer begins. Once the user manually toggles, their choice sticks.
  const manual = useRef(false);
  const [expanded, setExpanded] = useState(inProgress && !answerStarted);

  // Live "Worked for Xs" measurement: clock starts when the block first shows
  // live activity, freezes when the answer begins (the collapse moment — the
  // span the user actually watched Sara work). Restored turns never enter the
  // inProgress branch, so `measuredSeconds` stays null and the persisted-
  // timestamp span (workedSeconds prop) is used instead.
  const liveStartRef = useRef<number | null>(null);
  const [measuredSeconds, setMeasuredSeconds] = useState<number | null>(null);
  useEffect(() => {
    if (inProgress && !answerStarted && liveStartRef.current === null) {
      // Prefer the send-time anchor; mount time is the (undercounting)
      // fallback when the opener's timestamp isn't available.
      liveStartRef.current = turnStartedAt ?? Date.now();
    }
    if (answerStarted && liveStartRef.current !== null) {
      setMeasuredSeconds(
        (prev) => prev ?? (Date.now() - (liveStartRef.current as number)) / 1000
      );
    }
  }, [inProgress, answerStarted, turnStartedAt]);
  const effectiveWorkedSeconds = measuredSeconds ?? workedSeconds;

  useEffect(() => {
    if (manual.current) return;
    setExpanded(inProgress && !answerStarted);
  }, [inProgress, answerStarted]);

  const toggle = () => {
    manual.current = true;
    setExpanded((v) => !v);
  };

  const model = useMemo(
    () => buildThinkingModel(commentary, refs),
    [commentary, refs]
  );

  // "Still working" for LABEL purposes ends when the answer starts — the
  // backend's inProgress rides the turn-flush debounce ~1s past it, and the
  // header must not read "Working…" next to a streaming answer.
  const working = inProgress && !answerStarted;

  // Keep the outgoing branch mounted for the collapse transition to play out.
  // Without the linger, unmounting the timeline the instant `expanded` flips
  // false snaps its ~200px away in one frame — the single largest layout
  // shift of the first-message turn (flicker trace). Each branch sits in a
  // CollapseHeight wrapper (grid-rows 1fr↔0fr), so the block's total height
  // — and everything below it — moves smoothly instead.
  const renderTimeline = useLinger(expanded);
  const renderCollapsed = useLinger(!expanded);

  if (model.steps.length === 0) return null;

  return (
    <div data-component="thinking_block" className="mt-1 ml-1">
      <CollapseHeight open={!expanded}>
        {renderCollapsed && (
          <ThinkingCollapsed
            model={model}
            inProgress={working}
            workedSeconds={effectiveWorkedSeconds}
            liveTool={liveTool}
            onToggle={toggle}
          />
        )}
      </CollapseHeight>
      <CollapseHeight open={expanded}>
        {renderTimeline && (
          <FadeSlideIn>
            <ThinkingTimeline
              model={model}
              inProgress={working}
              workedSeconds={effectiveWorkedSeconds}
              liveTool={liveTool}
              onToggle={toggle}
            />
          </FadeSlideIn>
        )}
      </CollapseHeight>
    </div>
  );
}

/** How long the height transition runs. The linger holds the outgoing branch
 *  mounted slightly longer so the transition always completes. */
const COLLAPSE_MS = 200;
const LINGER_MS = COLLAPSE_MS + 50;

/** True while `active`, and for LINGER_MS afterwards — keeps a collapsing
 *  branch mounted until its height transition has finished. */
function useLinger(active: boolean): boolean {
  const [mounted, setMounted] = useState(active);
  useEffect(() => {
    if (active) {
      setMounted(true);
      return;
    }
    const id = setTimeout(() => setMounted(false), LINGER_MS);
    return () => clearTimeout(id);
  }, [active]);
  return active || mounted;
}

/**
 * Height-animating show/hide via the CSS grid `grid-template-rows: 1fr↔0fr`
 * technique — animates to/from the content's natural height with no
 * measurement. The inner div fades in step with the height so the outgoing
 * branch dissolves as it shrinks and the incoming one appears as its height
 * settles. `motion-safe:` keeps reduced-motion users on the old instant swap
 * (a 0fr wrapper hides its lingering child immediately when transitions are
 * off). Bootstrap collision note: `opacity-[0]`/`opacity-[1]` arbitrary
 * values, never `.opacity-0`/`.opacity-100` (Bootstrap ships those with
 * !important).
 */
function CollapseHeight({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'grid duration-200 ease-out motion-safe:transition-[grid-template-rows]',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        // During the linger both branches are mounted; the closing one must
        // not swallow clicks (its toggle sits where the incoming branch's
        // toggle lands, so a tap on the shrinking timeline re-expanded it)
        // nor stay in the a11y tree.
        !open && 'pointer-events-none'
      )}
      aria-hidden={!open}
    >
      <div
        className={cn(
          'min-h-0 overflow-hidden duration-200 ease-out motion-safe:transition-opacity',
          open ? 'opacity-[1]' : 'opacity-[0]'
        )}
      >
        {children}
      </div>
    </div>
  );
}
